import React, { useEffect, useState, useMemo } from 'react';
import Navigation from '../components/Navigation';
import { getTableCaptainApprovals, approveTableCaptainRequest, getAdminDashboardData, getAdditionalTableRequests, processAdditionalTableRequest, exportAllGuests } from '../api';

const Admin = () => {
  const [approvals, setApprovals] = useState([]);
  const [additionalTableRequests, setAdditionalTableRequests] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [approvalsData, dashData, additionalRequestsData] = await Promise.all([
        getTableCaptainApprovals(),
        getAdminDashboardData(),
        getAdditionalTableRequests()
      ]);
      setApprovals(Array.isArray(approvalsData) ? approvalsData : []);
      setDashboardData(dashData);
      setAdditionalTableRequests(Array.isArray(additionalRequestsData) ? additionalRequestsData : []);
    } catch (err) {
      console.error('Error loading admin data', err);
      setError('Unable to load admin dashboard data.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleApprove = async (id) => {
    if (!id) return;
    setProcessingId(id);
    try {
      await approveTableCaptainRequest(id);
      setSuccessMessage('Successfully approved table captain!');
      setApprovals((prev) => prev.filter((a) => a.id !== id));
      setTimeout(() => setSuccessMessage(''), 3000);

      // Refresh dashboard data to show new captain immediately
      const dashData = await getAdminDashboardData();
      setDashboardData(dashData);
    } catch (err) {
      console.error('Failed to approve', err);
      alert('Failed to approve captain. Check console for details.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleProcessAdditionalTable = async (captainId, decision) => {
    if (!captainId) return;
    setProcessingId(captainId);
    try {
      await processAdditionalTableRequest(captainId, decision);
      setSuccessMessage(`Successfully ${decision === 'Approve' ? 'approved' : 'denied'} additional table!`);
      setAdditionalTableRequests((prev) => prev.filter(r => r.CaptainId !== captainId));
      setTimeout(() => setSuccessMessage(''), 3000);

      // Refresh dashboard data
      const dashData = await getAdminDashboardData();
      setDashboardData(dashData);
    } catch (err) {
      console.error(`Failed to ${decision.toLowerCase()} additional table`, err);
      // alert(`Failed to ${decision.toLowerCase()} additional table.`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleExport = async () => {
    try {
      setSuccessMessage('Exporting guests...');
      const guests = await exportAllGuests();

      if (!guests || !guests.length) {
        alert('No guests to export.');
        setSuccessMessage('');
        return;
      }

      // Get all unique keys across all guests for CSV headers
      const allKeys = new Set();
      guests.forEach(g => {
        Object.keys(g).forEach(k => {
          if (typeof g[k] === 'object' && g[k] !== null && !Array.isArray(g[k])) {
            // Flatten nested objects like form_data safely using dot-notation
            Object.keys(g[k]).forEach(subKey => allKeys.add(`${k}.${subKey}`));
          } else {
            allKeys.add(k);
          }
        });
      });
      const headers = Array.from(allKeys);

      let csvContent = headers.join(',') + '\n';
      guests.forEach(g => {
        const row = headers.map(header => {
          let val;
          if (header.includes('.')) {
            const firstDotIndex = header.indexOf('.');
            const mainKey = header.substring(0, firstDotIndex);
            const subKey = header.substring(firstDotIndex + 1);
            if (g[mainKey] && typeof g[mainKey] === 'object' && !Array.isArray(g[mainKey])) {
              val = g[mainKey][subKey];
            }
          }
          if (val === undefined) {
            val = g[header];
          }
          if (val === undefined || val === null) val = '';
          // Handle arrays (e.g. roles)
          if (Array.isArray(val)) val = val.join(';');
          // escape quotes and wrap in quotes
          return `"${String(val).replace(/"/g, '""')}"`;
        });
        csvContent += row.join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'guests_export.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccessMessage('Export complete!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to export guests', err);
      alert('Failed to export guests.');
      setSuccessMessage('');
    }
  };


  // State for expanded captains (using a Set for efficient lookups)
  const [expandedCaptainIds, setExpandedCaptainIds] = useState(new Set());

  const toggleCaptainExpand = (id) => {
    setExpandedCaptainIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    if (dashboardData?.captains) {
      const allIds = new Set(dashboardData.captains.map(c => c.id));
      setExpandedCaptainIds(allIds);
    }
  };

  const collapseAll = () => {
    setExpandedCaptainIds(new Set());
  };

  const guestStats = useMemo(() => {
    if (!dashboardData?.captains) return { totalActive: 0, acceptanceRate: 0 };
    let invited = 0;
    let accepted = 0;
    dashboardData.captains.forEach(c => {
      invited += (c.guestCounts?.Invited || 0);
      accepted += (c.guestCounts?.Accepted || 0);
    });
    const totalActive = invited + accepted;
    const acceptanceRate = totalActive > 0 ? ((accepted / totalActive) * 100).toFixed(0) : 0;
    return { totalActive, acceptanceRate };
  }, [dashboardData]);

  return (
    <div className="admin-container">
      <div className={`success-modal ${successMessage ? 'show' : ''}`}>
        <span>✓</span> {successMessage}
      </div>
      <Navigation />
      <div className="admin-card">
        <header className="admin-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div>
              <h1>Admin Dashboard</h1>
              <p>Manage table captains, guests, and approvals.</p>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleExport}
                disabled={isLoading}
                style={{
                  background: 'rgba(52, 211, 153, 0.2)',
                  border: '1px solid rgba(52, 211, 153, 0.4)',
                  color: '#34d399',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  cursor: isLoading ? 'default' : 'pointer',
                  opacity: isLoading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>📥</span>
                Export Guests
              </button>
              <button
                onClick={fetchData}
                disabled={isLoading}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  cursor: isLoading ? 'default' : 'pointer',
                  opacity: isLoading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>🔄</span>
                {isLoading ? 'Refreshing...' : 'Refresh Data'}
              </button>
            </div>
          </div>
        </header>

        {error && (
          <div className="error-text" style={{ padding: '0 0 1.5rem', textAlign: 'center' }}>
            {error}
          </div>
        )}

        {/* Stats Section */}
        {dashboardData?.stats && (
          <div className="admin-stats-grid">
            <div className="stat-card">
              <h3>Total Captains</h3>
              <p className="stat-value">{dashboardData.stats.totalCaptains}</p>
            </div>
            <div className="stat-card">
              <h3>Total Tables</h3>
              <p className="stat-value">{dashboardData.stats.totalTables}</p>
            </div>
            <div className="stat-card">
              <h3>Registered Guests</h3>
              <p className="stat-value">{dashboardData.stats.totalGuests}</p>
            </div>
            <div className="stat-card">
              <h3>Total Invited & Accepted</h3>
              <p className="stat-value">{guestStats.totalActive}</p>
            </div>
            <div className="stat-card">
              <h3>Acceptance Rate</h3>
              <p className="stat-value">{guestStats.acceptanceRate}%</p>
            </div>
          </div>
        )}

        {/* Approvals Section */}
        <section className="admin-section">
          <h2>Pending Approvals</h2>
          {isLoading && <p className="admin-empty">Loading...</p>}
          {!isLoading && approvals.length === 0 && (
            <p className="admin-empty">No pending approvals.</p>
          )}
          {approvals.length > 0 && (
            <div className="admin-request-list">
              {approvals.map((approval) => (
                <div className="admin-request-card" key={approval.id}>
                  <div>
                    <h3>{approval.name}</h3>
                    <p style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>{approval.email}</p>
                  </div>
                  <div className="admin-actions">
                    <button
                      type="button"
                      onClick={() => handleApprove(approval.id)}
                      className="approve-btn"
                      disabled={processingId === approval.id}
                    >
                      {processingId === approval.id ? '...' : 'Approve'}
                    </button>
                    <button type="button" className="reject-btn">Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="admin-section">
          <h2>Additional Table Requests</h2>
          {isLoading && <p className="admin-empty">Loading...</p>}
          {!isLoading && additionalTableRequests.length === 0 && (
            <p className="admin-empty">No pending requests for additional tables.</p>
          )}
          {additionalTableRequests.length > 0 && (
            <div className="admin-request-list">
              {additionalTableRequests.map((req) => (
                <div className="admin-request-card" key={req.id}>
                  <div>
                    <h3>Captain ID: {req.CaptainId}</h3>
                    <h3>Captain Name: {req.CaptainName}</h3>
                    <p style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>Status: {req.status}</p>
                  </div>
                  <div className="admin-actions">
                    <button
                      type="button"
                      onClick={() => handleProcessAdditionalTable(req.CaptainId, 'Approve')}
                      className="approve-btn"
                      disabled={processingId === req.CaptainId}
                    >
                      {processingId === req.CaptainId ? '...' : 'Approve'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleProcessAdditionalTable(req.CaptainId, 'Deny')}
                      className="reject-btn"
                      disabled={processingId === req.CaptainId}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Captains Overview Section */}
        <section className="admin-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '0.5rem' }}>
            <h2 style={{ margin: 0, border: 'none', padding: 0 }}>Table Captains Overview</h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={expandAll}
                style={{ background: 'transparent', border: '1px solid rgba(255, 255, 255, 0.2)', color: '#d4d4d8', padding: '0.4rem 0.8rem', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                style={{ background: 'transparent', border: '1px solid rgba(255, 255, 255, 0.2)', color: '#d4d4d8', padding: '0.4rem 0.8rem', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                Collapse All
              </button>
            </div>
          </div>

          {!dashboardData?.captains?.length ? (
            <p className="admin-empty">No table captains found.</p>
          ) : (
            <div className="captains-list">
              {dashboardData.captains.map((captain) => (
                <div key={captain.id} className="captain-item-card">
                  <div
                    className="captain-header"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleCaptainExpand(captain.id)}
                  >
                    <div className="captain-info">
                      <h3>{captain.name}</h3>
                      <p>{captain.email}</p>
                    </div>
                    <div className="captain-meta" style={{ gap: '0.5rem' }}>
                      <span className="guest-count-badge" style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#d4d4d8' }} title="Invited">
                        Invited: {captain.guestCounts?.Invited || 0}
                      </span>
                      <span className="guest-count-badge" style={{ background: 'rgba(52, 211, 153, 0.2)', color: '#34d399' }} title="Accepted">
                        Accepted: {captain.guestCounts?.Accepted || 0}
                      </span>
                      <span className="guest-count-badge" style={{ background: 'rgba(248, 113, 113, 0.2)', color: '#fca5a5' }} title="Declined">
                        Declined: {captain.guestCounts?.Declined || 0}
                      </span>
                      {captain.guestCounts?.Revoked > 0 && (
                        <span className="guest-count-badge" style={{ background: 'rgba(113, 113, 122, 0.3)', color: '#a1a1aa' }} title="Revoked">
                          Revoked: {captain.guestCounts?.Revoked || 0}
                        </span>
                      )}
                      <span className={`expand-icon ${expandedCaptainIds.has(captain.id) ? 'open' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </div>

                  {expandedCaptainIds.has(captain.id) && (
                    <div className="captain-details" style={{ display: 'block', padding: '0 1.5rem 1.5rem' }}>
                      <h4 style={{ marginTop: '0', marginBottom: '1rem', fontSize: '0.85rem', color: '#71717a' }}>Guest List (Total: {captain.guests.length})</h4>
                      {captain.guests && captain.guests.length > 0 ? (
                        <div className="admin-guest-table">
                          {(() => {
                            // Group guests by table number
                            const guestsByTable = captain.guests.reduce((acc, guest) => {
                              const tableNum = guest.tableNumber || guest.table_number || 1;
                              if (!acc[tableNum]) acc[tableNum] = [];
                              acc[tableNum].push(guest);
                              return acc;
                            }, {});

                            return Object.keys(guestsByTable).sort((a, b) => a - b).map(tableNum => (
                              <div key={tableNum} style={{ marginBottom: '1.5rem' }}>
                                <h5 style={{ color: '#d4d4d8', fontSize: '0.8rem', textTransform: 'uppercase', marginBottom: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.25rem' }}>
                                  Table {tableNum}
                                </h5>
                                {guestsByTable[tableNum].sort((a, b) => {
                                  const statusOrder = { 'Accepted': 1, 'Invited': 2, 'Declined': 3 };
                                  const rankA = statusOrder[a.status] || 4;
                                  const rankB = statusOrder[b.status] || 4;
                                  return rankA - rankB;
                                }).map((guest) => (
                                  <div key={guest.id} className="admin-guest-row">
                                    <div className="guest-col name">{guest.name}</div>
                                    <div className="guest-col email">{guest.email}</div>
                                    <div className="guest-col status">
                                      <span className={`status-badge ${guest.status?.toLowerCase()}`}>
                                        {guest.status}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ));
                          })()}
                        </div>
                      ) : (
                        <p className="no-guests-msg">No guests invited yet.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Admin;
